import { useLocation, useNavigate, useParams } from 'react-router';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { TransactionDetails } from './ui/TransactionDetails/TransactionDetails';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { Button } from '@/components/Button/Button';
import PlusIcon from '@/images/PlusIcon.svg?react';
import { useEffect, useState } from 'react';
import { NoResults } from '@/components/NoResults/NoResults';
import NoWiresIcon from '@/images/NoWiresIcon.svg?react';
import { ModalWindow } from '@/components/ModalWindow/ModalWindow';
import { Paginator } from '@/components/Paginator/Paginator';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { useInterval } from '@/hooks/useInterval';
import {
  WIRE_TYPE_ALL,
  WIRE_TYPE_FILTER_AMPLITUDE_EVENTS_MAP,
  WIRE_TYPE_FILTER_CONFIG,
  WireTypesType,
} from './const/const';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { WIRE_TYPE_DB } from '@/const/wire';
import { useQuery, keepPreviousData, useQueryClient, useMutation } from '@tanstack/react-query';
import { createWire, deleteTransaction, loadTransactionDetails, loadTransactionWires } from './api/api';
import { isPendingWireStatus } from '@/helpers/wireHelpers';
import { Box } from '@/components/Box/Box';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import {
  SearchFilterRadioOptionType,
  TransactionsSearchFilters,
} from '@/bundle/_OrgAdmin/ui/TransactionsSearchFilters/TransactionsSearchFilters';
import {
  SEARCH_FILTER_RADIO_OPTION_TYPE,
  SearchFiltersType,
} from '@/bundle/_OrgAdmin/ui/TransactionsSearchFilters/const/const';
import { useFilterSearch } from '@/bundle/_OrgAdmin/hooks/useFilterSearch';
import { NoSearchResults } from '@/bundle/shared/components/NoSearchResults';
import { TransactionType } from '@/types/wireTypes';
import { useGetCurrencyOptions } from '@/bundle/shared/hooks/useGetCurrencyOptions';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { getTransactionsPath } from '../../path/path';
import { WiresList } from './ui/WiresList/WiresList';
import { WireTypeFilter } from './ui/WireTypeFilter/WireTypeFilter';
import { CreateWireForm } from './ui/CreateWireForm/CreateWireForm';
import { CreateWirePayloadType } from '@/api/v1/organization/contracts/createWireApi';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';
import { ONBOARDING_GUIDE_STEP_ID } from '@/context/OnboardingGuideContext/const';
import { useOnboardingGuide } from '@/context/OnboardingGuideContext/onboardingGuideContext';

const WIRES_POLLING_INTERVAL = 30000; // 30 seconds;

export const TransactionDetailsPage = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateBack } = useStatefulNavigation();
  const { id } = useParams<{ id: string }>();
  const { search, setSearch, submitSearch, clearSearch, searchFilters, changeSearchFilters, searchParams } =
    useFilterSearch();
  const { isViewModeSubscription, isInactiveSubscription } = useSubscriptionStatus();
  const { showStep, closeGuideStep } = useOnboardingGuide();

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenRemoveTransactionModal, setIsOpenRemoveTransactionModal] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState('');

  const { data: transactionData } = useQuery({
    queryKey: ['load_transaction_details', id],
    queryFn: () => loadTransactionDetails(id),
  });

  const { data: transactionWires, isPending: isPendingTransactionWire } = useQuery({
    queryKey: ['load_wires', id, searchParams],
    queryFn: () => loadTransactionWires(id, searchParams),
    placeholderData: keepPreviousData,
    enabled: !!transactionData?.body,
  });

  const { currencyOptions } = useGetCurrencyOptions(!!transactionWires?.body);

  const {
    mutate,
    data: createWireData,
    isPending,
    reset,
  } = useMutation({
    mutationKey: ['create_wire'],
    mutationFn: (payload: CreateWirePayloadType) => {
      return createWire(id, payload);
    },
    onSuccess(response) {
      if (response.error) return;

      closeGuideStep();
      setIsOpen(false);
      queryClient.resetQueries({ queryKey: ['load_wires'] });

      const eventData = {
        amount: response.body?.amount,
        currency: response.body?.currency,
      };
      const wireTypeEvent =
        response.body?.type === WIRE_TYPE_DB.inbound
          ? AMPLITUDE_EVENTS.CreateInboundWireSuccess
          : AMPLITUDE_EVENTS.CreateOutboundWireSuccess;

      amplitudeService.logEvent(wireTypeEvent, eventData);
    },
  });

  const { mutate: deleteTransactionMutate, isPending: isPendingDeleteTransaction } = useMutation({
    mutationKey: ['delete_transaction'],
    mutationFn: () => {
      return deleteTransaction(id);
    },
    onSuccess(response) {
      if (response?.error) {
        setIsOpenRemoveTransactionModal(false);
        queryClient.invalidateQueries({ queryKey: ['load_wires'] });

        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.RemoveTransactionSuccess);
      navigate(getTransactionsPath());
    },
  });

  useEffect(() => {
    showStep(ONBOARDING_GUIDE_STEP_ID.CREATE_WIRE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchFilters.byName) {
      return setSearchPlaceholder('Search by wire name');
    }

    if (searchFilters?.searchEmailTypes) {
      return setSearchPlaceholder('Search by email address');
    }
  }, [searchFilters]);

  const transaction = transactionData?.body || ({} as TransactionType);
  const selectedWireType = searchFilters?.type;
  const hasSearchName = !!searchParams.name;
  const { list, totalCount, hasData: hasWires, hasPagination } = getListData(transactionWires?.body, searchParams);

  const noAllTypeWires = !hasWires && selectedWireType === WIRE_TYPE_ALL && !isPendingTransactionWire && !hasSearchName;
  const hasWireInPendingStatus = list?.some(({ status }) => isPendingWireStatus(status));
  const noSearchResults = !hasWires && location.search && !isPendingTransactionWire;
  const isEmptyTransaction = !hasWires && !location.search && !isPendingTransactionWire;
  const showFilters = (hasWires && !isPendingTransactionWire) || (!hasWires && location.search);

  const confirmDeleteTransaction = () => {
    if (!id) {
      return;
    }

    deleteTransactionMutate();
  };

  const createWireHandler = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateWireRedirect);

    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    reset();
  };

  const selectWireType = (wireType: WireTypesType) => {
    amplitudeService.logEvent(WIRE_TYPE_FILTER_AMPLITUDE_EVENTS_MAP[wireType]);

    changeSearchFilters({ ...searchFilters, type: wireType });
  };

  const mergeSearchFilters = (filters: SearchFiltersType, type: SearchFilterRadioOptionType) => {
    changeSearchFilters(filters);

    if (type === SEARCH_FILTER_RADIO_OPTION_TYPE.NAME) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.TransactionDetailsSearchSelectNameFilter);
    }

    if (type === SEARCH_FILTER_RADIO_OPTION_TYPE.EMAIL) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.TransactionDetailsSearchSelectEmailFilter);
    }
  };

  const navigateToTransactions = () => {
    navigateBack(getTransactionsPath());
  };

  const handleSubmitSearch = () => {
    if (!!search.length && searchParams.name !== search) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.TransactionDetailsSearchSubmit);
    }

    submitSearch();
  };

  const openRemoveTransaction = () => {
    setIsOpenRemoveTransactionModal(true);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.RemoveTransactionRedirect);
  };

  // polling wire list logic
  const { cancelInterval, isIntervalRunning } = useInterval(() => {
    if (hasWires && hasWireInPendingStatus) {
      queryClient.invalidateQueries({ queryKey: ['load_wires'] });
    }
  }, WIRES_POLLING_INTERVAL);

  // Cancel polling logic
  useEffect(() => {
    const shouldCancelInterval = hasWires && !hasWireInPendingStatus && isIntervalRunning;

    if (shouldCancelInterval) {
      cancelInterval();
    }
  }, [hasWires, hasWireInPendingStatus, cancelInterval, isIntervalRunning]);

  return (
    <ContentLayout
      title='Transaction Details'
      breadcrumbs='Transactions > Details'
      backButton='Back to Transactions'
      onBack={navigateToTransactions}
      headerAction={
        noAllTypeWires && (
          <Button variant='secondary' width={160} mobileStretch onClick={openRemoveTransaction}>
            Remove
          </Button>
        )
      }
      apiError={transactionData?.error}
      banner={isInactiveSubscription && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        <Box display='flex' flexDirection='column' rowGap='62px'>
          <TransactionDetails transaction={transaction} />
          <Box>
            <Box flexDirection='column'>
              <Box justifyContent='space-between' alignItems='center' marginBottom='24px'>
                <Typography variant='headline4' fontWeight='medium' color={COLORS.black}>
                  Wires
                </Typography>

                <Button
                  disabled={isViewModeSubscription}
                  iconPosition='left'
                  icon={<PlusIcon className={isViewModeSubscription ? 'disabled-svg-icon' : ''} />}
                  width={160}
                  onClick={createWireHandler}
                  id={ONBOARDING_GUIDE_STEP_ID.CREATE_WIRE}
                >
                  Create Wire
                </Button>
              </Box>
              {showFilters && (
                <Box display='flex' alignItems='flex-start' columnGap='20px' flexWrap='wrap'>
                  <Box width='344px' mobile={{ width: '100%' }}>
                    <SearchInput
                      placeholder={searchPlaceholder}
                      value={search}
                      filters={
                        <TransactionsSearchFilters
                          nameLabel='Wire Name'
                          searchFilters={searchFilters}
                          onChangeSearchFilters={mergeSearchFilters}
                        />
                      }
                      onChange={(value) => setSearch(value)}
                      onClear={clearSearch}
                      onSubmit={handleSubmitSearch}
                    />
                  </Box>
                  <WireTypeFilter
                    selected={selectedWireType}
                    onSelect={selectWireType}
                    config={WIRE_TYPE_FILTER_CONFIG}
                  />
                </Box>
              )}
            </Box>
            {hasWires && <WiresList list={list} />}
            {isEmptyTransaction && (
              <NoResults marginTop={68} icon={<NoWiresIcon />} message='Please create your first wire.' />
            )}
            {noSearchResults && <NoSearchResults />}
          </Box>
        </Box>
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}

      <ModalWindow isOpen={isOpen} headerText='Create Wire' isStartPositionHeader width='504px'>
        <CreateWireForm
          onCreate={mutate}
          onClose={closeModal}
          currencyOptions={currencyOptions}
          selectedWireType={selectedWireType}
          isLoading={isPending}
          apiError={createWireData?.error}
        />
      </ModalWindow>

      <ConfirmModal
        isOpen={isOpenRemoveTransactionModal}
        isLoading={isPendingDeleteTransaction}
        header='Remove Transaction'
        body='You will not be able to work with this transaction once you remove it.'
        onClose={() => setIsOpenRemoveTransactionModal(false)}
        onConfirm={confirmDeleteTransaction}
      />
    </ContentLayout>
  );
};
